/**
 * Per-day segment clamp for TIMED multi-day events (#4401).
 *
 * Day/Week views render an event box from a `top` (hour-of-day of the start)
 * and a duration. For a multi-day TIMED event that geometry is only correct on
 * the start day — on the middle/last days the raw start/end would place the box
 * at the start hour (e.g. 9 AM) with the whole multi-day duration. This helper
 * clamps the event to the slice that falls on ONE rendered day, following the
 * Google-calendar convention:
 *   - start day:  start time → next local midnight (runs to the bottom of grid)
 *   - middle day: full grid (local midnight → next local midnight)
 *   - last day:   local midnight → end time
 *
 * Membership and the exclusive-end-at-local-midnight rule reuse
 * `getLastOccupiedDay` (so a 5pm→midnight slice never paints an extra day),
 * keeping this consistent with `getEventsOverlappingDay`. Returns `null` when
 * the event does not occupy the given day (or has no parseable start/end).
 *
 * All math is local-day arithmetic via `setDate`, which carries across month/
 * year and DST boundaries the same way the rest of the calendar does.
 */
import { parseEventDate, startOfLocalDay } from '../dateTimeUtils';
import { getLastOccupiedDay } from './getEventsForDate';

export interface DaySegment {
  /** Clamped start of the event's slice on this day (local time). */
  start: Date;
  /** Clamped end of the event's slice on this day (local time). */
  end: Date;
}

export function getDaySegment(
  event: { allDay?: boolean; startAt: string; endAt?: string },
  day: Date,
): DaySegment | null {
  const start = parseEventDate(event.startAt);
  const end = parseEventDate(event.endAt);
  if (!start || !end) return null;

  const dayStart = startOfLocalDay(day);
  const nextDayStart = startOfLocalDay(day);
  nextDayStart.setDate(nextDayStart.getDate() + 1);

  // Membership: the day must fall within [startDay, lastOccupiedDay], using the
  // same exclusive-midnight semantics as getEventsOverlappingDay.
  const firstDay = startOfLocalDay(start);
  const lastOccupied = getLastOccupiedDay(event);
  if (!lastOccupied) return null;
  if (dayStart.getTime() < firstDay.getTime()) return null;
  if (dayStart.getTime() > lastOccupied.getTime()) return null;

  // Clamp the event's [start, end] to this day's [midnight, next-midnight].
  const segStart = start.getTime() > dayStart.getTime() ? start : new Date(dayStart);
  const segEnd = end.getTime() < nextDayStart.getTime() ? end : new Date(nextDayStart);

  return { start: segStart, end: segEnd };
}
